const app = () => {

    const wishlistEnabled =
        typeof document !== 'undefined' &&
        document.body &&
        document.body.dataset.enableWishlist === 'true';

    if (wishlistEnabled) {
        /**
         * Wishlist module
         * - Persists product handles in localStorage
         * - Emits `wishlist:updated` CustomEvent on window with detail { items }
         */
        const WISHLIST_STORAGE_KEY = 'stefan_lindfors:wishlist:product_handles';

        const readWishlist = () => {
            try {
                const raw = window.localStorage.getItem(WISHLIST_STORAGE_KEY);
                if (!raw) return [];
                const parsed = JSON.parse(raw);
                if (!Array.isArray(parsed)) return [];

                // Normalize and de-duplicate any legacy values
                const uniqueItems = Array.from(new Set(parsed.filter(Boolean)));
                if (uniqueItems.length !== parsed.length) {
                    window.localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(uniqueItems));
                }
                return uniqueItems;
            } catch (e) {
                return [];
            }
        };

        const writeWishlist = (items) => {
            try {
                const uniqueItems = Array.from(new Set(items.filter(Boolean)));
                window.localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(uniqueItems));
                const event = new CustomEvent('wishlist:updated', { detail: { items: uniqueItems } });
                window.dispatchEvent(event);
                return uniqueItems;
            } catch (e) {
                return items;
            }
        };

        const getWishlist = () => readWishlist();

        const isInWishlist = (handle) => {
            if (!handle) return false;
            return readWishlist().includes(handle);
        };

        const toggleWishlistItem = (handle) => {
            if (!handle) return readWishlist();
            const current = readWishlist();
            const index = current.indexOf(handle);
            if (index === -1) {
                current.push(handle);
            } else {
                current.splice(index, 1);
            }
            return writeWishlist(current);
        };

        const subscribeToWishlist = (callback) => {
            if (typeof callback !== 'function') return () => {};
            const handler = (event) => {
                callback(event.detail && Array.isArray(event.detail.items) ? event.detail.items : readWishlist());
            };
            window.addEventListener('wishlist:updated', handler);
            // Call immediately with current state
            callback(readWishlist());
            return () => window.removeEventListener('wishlist:updated', handler);
        };

        // Expose wishlist API globally for other scripts / inline JS
        window.StefanLindforsWishlist = {
            get: getWishlist,
            toggle: toggleWishlistItem,
            has: isInWishlist,
            subscribe: subscribeToWishlist,
            key: WISHLIST_STORAGE_KEY,
        };
    }

    /**
     * Product cards: play native video or enable autoplay on external embed while hovered.
     */
    const initProductCardHoverVideos = (root) => {
        const container = root && root.querySelectorAll ? root : document;
        const wrappers = container.querySelectorAll('.image-wrapper--hover-video-bg');
        const reducedMotion =
            typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const enhanceExternalIframe = (iframe) => {
            if (!iframe || iframe.dataset.productCardVideoBaseSrc) return;
            const raw = iframe.getAttribute('src');
            if (!raw) return;
            iframe.dataset.productCardVideoBaseSrc = raw;
            try {
                const u = new URL(raw, window.location.origin);
                const host = u.hostname;
                if (host.includes('youtube.com') || host === 'youtu.be') {
                    u.searchParams.set('autoplay', '1');
                    u.searchParams.set('mute', '1');
                    u.searchParams.set('playsinline', '1');
                    iframe.dataset.productCardVideoPlaySrc = u.toString();
                } else if (host.includes('vimeo.com')) {
                    u.searchParams.set('autoplay', '1');
                    u.searchParams.set('muted', '1');
                    iframe.dataset.productCardVideoPlaySrc = u.toString();
                }
            } catch (e) {
                /* keep base src only */
            }
        };

        wrappers.forEach((wrapper) => {
            if (wrapper.dataset.productCardHoverVideoBound) return;
            wrapper.dataset.productCardHoverVideoBound = 'true';
            const card = wrapper.closest('.card');
            if (!card) return;

            const video = wrapper.querySelector('video.product-card__hover-video-el');
            const iframe = wrapper.querySelector('iframe');
            if (iframe) {
                enhanceExternalIframe(iframe);
            }

            const play = () => {
                if (reducedMotion) return;
                if (video) {
                    video.muted = true;
                    const p = video.play();
                    if (p && typeof p.catch === 'function') {
                        p.catch(() => {});
                    }
                }
                if (iframe && iframe.dataset.productCardVideoPlaySrc) {
                    iframe.src = iframe.dataset.productCardVideoPlaySrc;
                }
            };

            const stop = () => {
                if (video) {
                    video.pause();
                    try {
                        video.currentTime = 0;
                    } catch (e) {
                        /* ignore */
                    }
                }
                if (iframe && iframe.dataset.productCardVideoBaseSrc) {
                    iframe.src = iframe.dataset.productCardVideoBaseSrc;
                }
            };

            card.addEventListener('mouseenter', play);
            card.addEventListener('mouseleave', stop);
            card.addEventListener('focusin', play);
            card.addEventListener('focusout', (ev) => {
                if (!card.contains(ev.relatedTarget)) stop();
            });
        });
    };

    window.initProductCardHoverVideos = initProductCardHoverVideos;

    const header = document.querySelector('.header');
    const headerHamburger = document.querySelector('.header__hamburger');
    const mobileMenuClose = document.querySelector('.mobile-menu__close');

    if(header) {

        const scrollPoint = document.querySelector('.header-scroll-point');
        const isTransparent = header.classList.contains('header--transparent');

        function scrollObserverCallback(payload) {
            if (payload[0].intersectionRatio) {
                // header.classList.remove('header--scroll');
                
                if (isTransparent) {
                    header.classList.remove('header--solid');
                }
            } else {
                // header.classList.add('header--scroll');
                
                if (isTransparent) {
                    header.classList.add('header--solid');
                }
            }
        }
    
        const scrollObserver = new IntersectionObserver(scrollObserverCallback);
        scrollObserver.observe(scrollPoint);

        if (isTransparent) {
            const solidScrollThreshold = 50;
            
            function handleTransparentScroll() {
                if (window.scrollY > solidScrollThreshold) {
                    header.classList.add('header--solid');
                } else {
                    header.classList.remove('header--solid');
                }
            }

            window.addEventListener('scroll', handleTransparentScroll, { passive: true });
            
            handleTransparentScroll();
        }

    }

    if (headerHamburger) {

        headerHamburger.addEventListener('click', event => {
            event.preventDefault();

            headerHamburger.classList.toggle('header__hamburger--active');
            document.body.classList.toggle('mobile-menu--active');

            const headerHamburgerExpanded = headerHamburger.getAttribute('aria-expanded');

            if(headerHamburgerExpanded == 'true') {

                headerHamburger.setAttribute('aria-expanded', 'false');
                headerHamburger.setAttribute('aria-label', 'Open menu');

            } else {

                headerHamburger.setAttribute('aria-expanded', 'true');
                headerHamburger.setAttribute('aria-label', 'Close menu');

            }
        });

        if (mobileMenuClose) {

            mobileMenuClose.addEventListener('focus', () => { 

                headerHamburger.focus();

            });
        
        }

        // Mobile menu accordion toggles
        const mobileNavToggles = document.querySelectorAll('.mobile-nav__toggle');
        
        mobileNavToggles.forEach(toggle => {
            toggle.addEventListener('click', function() {
                const navItem = this.closest('.nav-item');
                const submenu = navItem.querySelector('.mobile-nav__submenu');
                const isExpanded = this.getAttribute('aria-expanded') === 'true';
                
                // Toggle aria-expanded
                this.setAttribute('aria-expanded', !isExpanded ? 'true' : 'false');
                
                // Toggle active class on nav item
                navItem.classList.toggle('nav-item--active');
                
                // Toggle submenu visibility
                if (isExpanded) {
                    submenu.setAttribute('hidden', '');
                } else {
                    submenu.removeAttribute('hidden');
                }
            });
        });

    }

    /**
     * Wishlist UI bindings
     */
    if (wishlistEnabled && window.StefanLindforsWishlist) {
        const wishlistCountEl = document.querySelector('[data-wishlist-count]');
        const wishlistGrid = document.querySelector('[data-wishlist-grid]');
        const wishlistEmptyState = document.querySelector('[data-wishlist-empty]');

        const applyButtonState = (button, items) => {
            const handle = button.getAttribute('data-product-handle');
            if (!handle) return;
            const active = items.includes(handle);
            button.classList.toggle('wishlist-button--active', active);
            button.setAttribute('aria-pressed', active ? 'true' : 'false');
        };

        const bindWishlistButtonHandlers = () => {
            const buttons = document.querySelectorAll('[data-wishlist-toggle]');
            buttons.forEach((button) => {
                if (button.dataset.wishlistBound === 'true') return;
                button.dataset.wishlistBound = 'true';

                button.addEventListener('click', (event) => {
                    event.preventDefault();
                    const handle = button.getAttribute('data-product-handle');
                    if (!handle) return;
                    const updated = window.StefanLindforsWishlist.toggle(handle);
                    applyButtonState(button, updated);
                    syncHeaderCount(updated);
                });
            });
        };

        const syncAllButtons = (items) => {
            const buttons = document.querySelectorAll('[data-wishlist-toggle]');
            if (buttons.length > 0) {
                buttons.forEach((button) => applyButtonState(button, items));
            }
        };

        const syncHeaderCount = (items) => {
            if (!wishlistCountEl) return;
            const count = items.length;
            wishlistCountEl.textContent = count;
            if (count > 0) {
                wishlistCountEl.removeAttribute('hidden');
            } else {
                wishlistCountEl.setAttribute('hidden', '');
            }
        };

        const syncWishlistGrid = async (items) => {
            if (!wishlistGrid) return;
            wishlistGrid.innerHTML = '';

            if (items.length === 0) {
                if (wishlistEmptyState) {
                    wishlistEmptyState.style.display = '';
                }
                return;
            }

            if (wishlistEmptyState) {
                wishlistEmptyState.style.display = 'none';
            }

            const fragment = document.createDocumentFragment();

            for (const handle of items) {
                try {
                    const response = await fetch(`/products/${encodeURIComponent(handle)}?view=wishlist`);
                    if (!response.ok) continue;
                    const html = await response.text();
                    const wrapper = document.createElement('div');
                    wrapper.innerHTML = html;
                    const card = wrapper.firstElementChild;
                    if (card) {
                        fragment.appendChild(card);
                    }
                } catch (e) {
                    // ignore individual failures
                }
            }

            wishlistGrid.appendChild(fragment);

            // After cards are rendered (including wishlist page), sync heart states and bind handlers
            syncAllButtons(items);
            bindWishlistButtonHandlers();
        };

        // Initial state
        /*
        const initialItems = window.StefanLindforsWishlist.get();
        syncWishlistGrid(initialItems);
        syncAllButtons(initialItems);
        syncHeaderCount(initialItems);
        */

        // Listen for global wishlist updates
        window.StefanLindforsWishlist.subscribe((items) => {
            syncWishlistGrid(items);
            syncAllButtons(items);
            syncHeaderCount(items);
        });

        // Initial click handlers for any wishlist buttons already in the DOM
        bindWishlistButtonHandlers();
    }

    /**
     * Store locator & generic maps (Leaflet + OSM)
     */
    const storeLocatorSections = document.querySelectorAll('[data-store-locator]');
    const genericMapCanvases = document.querySelectorAll('.map__canvas');

    if (storeLocatorSections.length > 0 || genericMapCanvases.length > 0) {
        const LEAFLET_CSS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        const LEAFLET_JS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

        const loadLeaflet = () => {
            return new Promise((resolve, reject) => {
                if (window.L && typeof window.L.map === 'function') {
                    resolve(window.L);
                    return;
                }

                if (!document.querySelector('link[data-leaflet-css]')) {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = LEAFLET_CSS_URL;
                    link.setAttribute('data-leaflet-css', 'true');
                    document.head.appendChild(link);
                }

                const existingScript = document.querySelector('script[data-leaflet-js]');
                if (existingScript && existingScript.dataset.loaded === 'true') {
                    resolve(window.L);
                    return;
                }

                const script = existingScript || document.createElement('script');
                script.src = LEAFLET_JS_URL;
                script.async = true;
                script.defer = true;
                script.setAttribute('data-leaflet-js', 'true');

                script.onload = () => {
                    script.dataset.loaded = 'true';
                    if (window.L && typeof window.L.map === 'function') {
                        resolve(window.L);
                    } else {
                        reject(new Error('Leaflet failed to load'));
                    }
                };

                script.onerror = () => reject(new Error('Leaflet script failed to load'));

                if (!existingScript) {
                    document.head.appendChild(script);
                }
            });
        };

        const createThemeMarkerIcon = (L) => {
            try {
                const rootStyles = getComputedStyle(document.documentElement);
                const accentColor = rootStyles.getPropertyValue('--color-accent').trim() ||
                    rootStyles.getPropertyValue('--color-primary-button-background').trim() ||
                    '#0052cc';

                const svg = encodeURIComponent(
                    `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                      <defs>
                        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                          <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-color="rgba(0,0,0,0.35)"/>
                        </filter>
                      </defs>
                      <g filter="url(#shadow)">
                        <path fill="${accentColor}" d="M16 2C10.48 2 6 6.48 6 12c0 7.09 7.02 13.31 9.64 15.35.22.18.5.28.79.28s.57-.1.79-.28C18.98 25.31 26 19.09 26 12 26 6.48 21.52 2 16 2z"/>
                        <circle cx="16" cy="12" r="4.5" fill="#ffffff"/>
                      </g>
                    </svg>`
                );

                return L.icon({
                    iconUrl: `data:image/svg+xml,${svg}`,
                    iconSize: [32, 32],
                    iconAnchor: [16, 30],
                    popupAnchor: [0, -28],
                    tooltipAnchor: [0, -28],
                });
            } catch (e) {
                return L.icon({
                    iconUrl:
                        'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
                    iconRetinaUrl:
                        'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
                    shadowUrl:
                        'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    tooltipAnchor: [16, -28],
                    shadowSize: [41, 41],
                });
            }
        };

        const initStoreLocatorSection = (sectionEl, L) => {
            if (!sectionEl) return;

            const mapEl = sectionEl.querySelector('[data-store-locator-map]');
            const listEl = sectionEl.querySelector('[data-store-locator-list]');
            if (!mapEl || !listEl) return;

            const items = Array.from(listEl.querySelectorAll('.store-locator__item'));
            if (items.length === 0) return;

            const rawCenterLat = parseFloat(mapEl.dataset.defaultCenterLat || '');
            const rawCenterLng = parseFloat(mapEl.dataset.defaultCenterLng || '');
            const initialZoom = parseInt(mapEl.dataset.initialZoom || '5', 10) || 5;

            let centerLat = isNaN(rawCenterLat) ? null : rawCenterLat;
            let centerLng = isNaN(rawCenterLng) ? null : rawCenterLng;

            if (centerLat === null || centerLng === null) {
                const firstWithCoords = items.find((item) => {
                    const lat = parseFloat(item.dataset.lat || '');
                    const lng = parseFloat(item.dataset.lng || '');
                    return !isNaN(lat) && !isNaN(lng);
                });

                if (firstWithCoords) {
                    centerLat = parseFloat(firstWithCoords.dataset.lat);
                    centerLng = parseFloat(firstWithCoords.dataset.lng);
                }
            }

            if (centerLat === null || centerLng === null) {
                return;
            }

            const map = L.map(mapEl).setView([centerLat, centerLng], initialZoom);

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution:
                    '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
            }).addTo(map);

            const markers = [];
            const markerIcon = createThemeMarkerIcon(L);
            const bounds = L.latLngBounds();

            const activateItem = (item) => {
                items.forEach((el) => el.classList.remove('store-locator__item--active'));
                if (item) {
                    item.classList.add('store-locator__item--active');
                    item.scrollIntoView({ block: 'nearest' });
                }
            };

            items.forEach((item) => {
                const lat = parseFloat(item.dataset.lat || '');
                const lng = parseFloat(item.dataset.lng || '');
                if (isNaN(lat) || isNaN(lng)) {
                    return;
                }

                const name = item.dataset.name || '';
                const city = item.dataset.city || '';
                const country = item.dataset.country || '';

                const marker = L.marker([lat, lng], { icon: markerIcon }).addTo(map);

                const popupName = name || 'Store';
                const popupLocationLine = [city, country].filter(Boolean).join(', ');

                const popupHtml = `
                    <div class="store-locator-popup">
                      <div class="store-locator-popup__name h6">${popupName}</div>
                      ${
                          popupLocationLine
                              ? `<div class="store-locator-popup__location text-small">${popupLocationLine}</div>`
                              : ''
                      }
                    </div>
                `;

                marker.bindPopup(popupHtml.trim());

                markers.push({ marker, item });
                bounds.extend(marker.getLatLng());

                item.addEventListener('click', () => {
                    activateItem(item);
                    map.setView(marker.getLatLng(), Math.max(map.getZoom(), 10));
                    marker.openPopup();
                });

                item.addEventListener('keydown', (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        activateItem(item);
                        map.setView(marker.getLatLng(), Math.max(map.getZoom(), 10));
                        marker.openPopup();
                    }
                });

                marker.on('click', () => {
                    activateItem(item);
                });
            });

            if (!bounds.isValid()) {
                map.setView([centerLat, centerLng], initialZoom);
            } else if (items.length > 1) {
                map.fitBounds(bounds, { padding: [40, 40] });
            }

            const searchInput = sectionEl.querySelector('[data-store-locator-search]');
            const filtersContainer = sectionEl.querySelector('[data-store-locator-filters]');
            const noResultsEl = sectionEl.querySelector('[data-store-locator-no-results]');

            const applyFilterState = (searchTerm, activeTag) => {
                const normalizedSearch = (searchTerm || '').toLowerCase().trim();
                let visibleCount = 0;

                items.forEach((item, index) => {
                    const name = (item.dataset.name || '').toLowerCase();
                    const city = (item.dataset.city || '').toLowerCase();
                    const region = (item.dataset.region || '').toLowerCase();
                    const country = (item.dataset.country || '').toLowerCase();
                    const tags = (item.dataset.tags || '').toLowerCase();

                    const matchesSearch =
                        !normalizedSearch ||
                        name.includes(normalizedSearch) ||
                        city.includes(normalizedSearch) ||
                        region.includes(normalizedSearch) ||
                        country.includes(normalizedSearch) ||
                        tags.includes(normalizedSearch);

                    const matchesTag =
                        !activeTag ||
                        tags
                            .split(',')
                            .some(
                                (tag) =>
                                    tag
                                        .replace(/[\[\]"]/g, '')
                                        .trim()
                                        .toLowerCase() === activeTag
                            );

                    const isVisible = matchesSearch && matchesTag;

                    item.hidden = !isVisible;
                    const pair = markers[index];
                    if (pair && pair.marker) {
                        if (isVisible) {
                            pair.marker.addTo(map);
                        } else {
                            map.removeLayer(pair.marker);
                        }
                    }

                    if (isVisible) {
                        visibleCount += 1;
                    }
                });

                if (noResultsEl) {
                    if (visibleCount === 0) {
                        noResultsEl.hidden = false;
                    } else {
                        noResultsEl.hidden = true;
                    }
                }
            };

            let currentTag = '';

            if (filtersContainer) {
                const allTags = new Map();
                items.forEach((item) => {
                    const tags = item.dataset.tags || '';
                    if (!tags) return;
                    tags.split(',').forEach((tag) => {
                        const cleaned = tag.replace(/[\[\]"]/g, '').trim();
                        if (cleaned) {
                            const key = cleaned.toLowerCase();
                            if (!allTags.has(key)) {
                                allTags.set(key, cleaned);
                            }
                        }
                    });
                });

                if (allTags.size > 0) {
                    const createFilterButton = (rawLabel, value) => {
                        const button = document.createElement('button');
                        button.type = 'button';
                        button.className = 'button button--secondary store-locator__filter-button';

                        const cleanedLabel = rawLabel.replace(/[\[\]"]/g, '').trim();
                        button.textContent = cleanedLabel || rawLabel;
                        button.dataset.filterValue = value;
                        return button;
                    };

                    const allButton = createFilterButton(
                        window.theme && window.theme.strings && window.theme.strings.storeLocatorAll
                            ? window.theme.strings.storeLocatorAll
                            : 'All',
                        ''
                    );
                    allButton.classList.add('store-locator__filter-button--active');
                    filtersContainer.appendChild(allButton);

                    allTags.forEach((label, key) => {
                        const button = createFilterButton(label, key);
                        filtersContainer.appendChild(button);
                    });

                    filtersContainer.addEventListener('click', (event) => {
                        const target = event.target;
                        if (!(target instanceof HTMLElement)) return;
                        const value = target.dataset.filterValue;
                        if (typeof value === 'undefined') return;

                        currentTag = value;

                        const buttons = filtersContainer.querySelectorAll('.store-locator__filter-button');
                        buttons.forEach((btn) => {
                            btn.classList.toggle(
                                'store-locator__filter-button--active',
                                btn.dataset.filterValue === currentTag
                            );
                        });

                        applyFilterState(searchInput ? searchInput.value : '', currentTag);
                    });
                }
            }

            if (searchInput) {
                searchInput.addEventListener('input', () => {
                    applyFilterState(searchInput.value, currentTag);
                });
            }

            applyFilterState(searchInput ? searchInput.value : '', currentTag);
        };

        const initGenericMapCanvas = (canvasEl, L) => {
            if (!canvasEl) return;

            const lat = parseFloat(canvasEl.dataset.latitude || '');
            const lng = parseFloat(canvasEl.dataset.longitude || '');
            const zoom = parseInt(canvasEl.dataset.zoom || '15', 10) || 15;
            const title = canvasEl.dataset.markerTitle || '';

            if (isNaN(lat) || isNaN(lng)) return;

            const map = L.map(canvasEl).setView([lat, lng], zoom);

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution:
                    '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
            }).addTo(map);

            L.marker([lat, lng]).addTo(map).bindPopup(title || '');
        };

        loadLeaflet()
            .then((L) => {
                storeLocatorSections.forEach((sectionEl) => {
                    initStoreLocatorSection(sectionEl, L);
                });

                genericMapCanvases.forEach((canvasEl) => {
                    initGenericMapCanvas(canvasEl, L);
                });
            })
            .catch(() => {
                // If Leaflet fails to load, gracefully do nothing
            });
    }

};

document.addEventListener('DOMContentLoaded', () => {
    app();
    if (typeof window.initProductCardHoverVideos === 'function') {
        window.initProductCardHoverVideos(document);
    }
});